import { useQuery } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { fmt } from '../../../lib/format';
import { errorMessage } from '../../../lib/programs';
import type { CountRow, EventStats } from '../../../lib/events';
import { shortDay } from '../../../lib/eventInfo';
import type { SimplePanelProps } from '../adapt';
import WordCloud from './WordCloud';
import CommentCarousel from './CommentCarousel';

function Counts({ title, rows }: { title: string; rows: CountRow[] }) {
  const shown = rows.filter((r) => r.count > 0);
  if (!shown.length) return null;
  return (
    <div>
      <div className="text-small text-ink-2 mb-1">{title}</div>
      <ul>
        {shown.map((r) => (
          <li key={r.value} className="flex justify-between border-b border-line py-[4px]">
            <span>{r.value}</span>
            <span className="tabular">{fmt(r.count)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The deck's extra figures (After → Stats & report): survey responders apart from attendees, sector, membership, per session, comment themes. */
export default function StatsExtrasPanel({ eventId }: SimplePanelProps) {
  const q = useQuery<EventStats>(
    ['event-stats', eventId],
    async () => (await api.get(`/events/${eventId}/stats`)).data.data,
  );
  if (q.isLoading) return <SkeletonRows rows={4} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the figures.')}</ErrorNotice>;
  const x = q.data.extras;
  if (!x) return null;
  const r = x.responders;
  const m = x.membership;
  return (
    <Panel>
      <PanelHeader title="More for the report" note={`${fmt(r.responses)} survey response(s)`} />
      <PanelBody className="text-small grid gap-5 md:grid-cols-2">
        <Counts
          title="Survey responders"
          rows={[
            { value: 'Different people', count: r.people },
            { value: 'Attended in person', count: r.attendedInPerson },
            { value: 'Attended online', count: r.attendedOnline },
            { value: 'No attendance recorded', count: r.noAttendanceRecorded },
          ]}
        />
        <Counts
          title="Membership of those in person"
          rows={[
            { value: 'Members', count: m.members },
            { value: 'Guests (not members)', count: m.guests },
            { value: 'Speakers', count: m.speakers },
            ...m.fellowCohorts.map((c) => ({ value: `Fellows, ${c.value}`, count: c.count })),
          ]}
        />
        <Counts title="Organization type (sector)" rows={x.bySector} />
        {x.bySession.length ? (
          <div>
            <div className="text-small text-ink-2 mb-1">
              Feedback by session{x.unassignedFeedback ? ` · ${x.unassignedFeedback} not matched` : ''}
            </div>
            <ul>
              {x.bySession.map((s) => (
                <li key={s.sessionId} className="border-b border-line py-[4px]">
                  {shortDay(s.day)} {s.startTime} · {s.title}{' '}
                  <span className="text-ink-2">
                    · {s.responses} · {s.numeric.map((n) => `${n.label} ${n.mean ?? '—'}`).join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {x.themes ? (
          <div className="md:col-span-2">
            <div className="text-small text-ink-2 mb-1">Words that come back in {x.comments} comment(s)</div>
            <WordCloud words={x.themes} unit="comments" />
          </div>
        ) : (
          <p className="text-ink-2">{x.comments} comment(s); admins see their themes.</p>
        )}
        {x.commentList?.length ? (
          <div className="md:col-span-2">
            <div className="text-small text-ink-2 mb-1">Comments</div>
            <CommentCarousel comments={x.commentList} />
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}
